/**
 * VÉRAULT Website CMS — Block Renderer (Shared)
 *
 * This single renderer is used in three contexts:
 *   1. The editor preview pane (live, against draft_blocks)
 *   2. The /preview/:slug full-page route (draft, with banner)
 *   3. A future live cutover on the public site (published_blocks)
 *
 * Branding is locked. Each block consumes ONLY whitelisted content props
 * from the block catalog. Colours, typography, and spacing are all derived
 * from the existing design system — never overrideable from the editor.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const cls = {
  // Section vertical rhythm — borrowed from the live public site
  sectionPad: "py-20 sm:py-24 lg:py-28 px-6",
  sectionPadLg: "py-24 sm:py-32 lg:py-36 px-6",
  // Container widths
  container: "max-w-5xl mx-auto",
  prose: "max-w-2xl mx-auto",
  // Editorial helpers
  eyebrow: "text-xs uppercase tracking-[0.22em] text-[#999] font-mono",
  serif: "font-serif text-[#1A1A1A]",
  body: "text-[#444] font-body leading-relaxed",
  italic: "font-accent italic",
};

// ---- Hero -----------------------------------------------------------
function BlockHero({ props }) {
  const align = props.align === "left" ? "text-left" : "text-center mx-auto";
  const wrap = `${cls.sectionPadLg} ${cls.container} ${align}`;
  return (
    <section className={wrap} data-block="hero">
      {props.image_url && (
        <div className="relative -mt-12 sm:-mt-20 mb-12 sm:mb-16 aspect-[21/9] rounded-md overflow-hidden">
          <img src={props.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      )}
      {props.eyebrow && (
        <p className={`${cls.eyebrow} mb-5`}>{props.eyebrow}</p>
      )}
      {props.title && (
        <h1 className={`${cls.serif} text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] ${align === "text-left" ? "" : "max-w-3xl mx-auto"}`}>
          {props.title}
        </h1>
      )}
      {props.intro && (
        <p className={`${cls.body} mt-6 text-lg ${align === "text-left" ? "max-w-2xl" : "max-w-2xl mx-auto"}`}>
          {props.intro}
        </p>
      )}
      {props.cta_label && props.cta_href && (
        <div className={`mt-10 ${align === "text-left" ? "" : "flex justify-center"}`}>
          <Link
            to={props.cta_href}
            className="inline-flex items-center gap-2 px-7 py-3 border border-[#1A1A1A] text-[#1A1A1A] font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-white transition-colors duration-300"
          >
            {props.cta_label}
            <ArrowRight className="size-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      )}
    </section>
  );
}

// ---- Text section ---------------------------------------------------
function BlockText({ props }) {
  const align = props.align === "center" ? "text-center" : "text-left";
  return (
    <section className={`${cls.sectionPad} ${align}`} data-block="text">
      <div className={`${align === "text-center" ? cls.prose : "max-w-3xl mx-auto"}`}>
        {props.heading && (
          <h2 className={`${cls.serif} text-3xl sm:text-4xl mb-6`}>
            {props.heading}
          </h2>
        )}
        {props.body && (
          <div className={`${cls.body} text-[17px] whitespace-pre-line`}>
            {props.body}
          </div>
        )}
      </div>
    </section>
  );
}

// ---- Image + Text ---------------------------------------------------
function BlockImageText({ props }) {
  const imgOnLeft = props.image_position !== "right";
  return (
    <section className={`${cls.sectionPad}`} data-block="image_text">
      <div className={`${cls.container} grid md:grid-cols-2 gap-10 lg:gap-16 items-center`}>
        <div className={`aspect-[4/3] overflow-hidden rounded-md bg-[#F5F2EE] ${imgOnLeft ? "" : "md:order-2"}`}>
          {props.image_url ? (
            <img src={props.image_url} alt={props.image_alt || ""} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#bbb] font-mono text-xs uppercase tracking-[0.18em]">
              Image
            </div>
          )}
        </div>
        <div className={imgOnLeft ? "" : "md:order-1"}>
          {props.heading && (
            <h2 className={`${cls.serif} text-3xl sm:text-4xl mb-5`}>{props.heading}</h2>
          )}
          {props.body && (
            <p className={`${cls.body} text-[16px] whitespace-pre-line`}>{props.body}</p>
          )}
          {props.cta_label && props.cta_href && (
            <Link
              to={props.cta_href}
              className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#1A1A1A] border-b border-[#1A1A1A] pb-1 hover:gap-3 transition-all"
            >
              {props.cta_label}
              <ArrowRight className="size-3" strokeWidth={1.5} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

// ---- CTA band -------------------------------------------------------
function BlockCTA({ props }) {
  return (
    <section className={`${cls.sectionPad} text-center bg-[#F5F2EE]`} data-block="cta">
      {props.heading && (
        <h2 className={`${cls.serif} text-3xl sm:text-4xl max-w-2xl mx-auto`}>{props.heading}</h2>
      )}
      {props.body && (
        <p className={`${cls.body} mt-4 text-[17px] max-w-xl mx-auto`}>{props.body}</p>
      )}
      {props.cta_label && props.cta_href && (
        <div className="mt-8">
          <Link
            to={props.cta_href}
            className="inline-flex items-center gap-2 px-7 py-3 bg-[#1A1A1A] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#333] transition-colors"
          >
            {props.cta_label}
            <ArrowRight className="size-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      )}
    </section>
  );
}

// ---- Spacer ---------------------------------------------------------
function BlockSpacer({ props }) {
  const h = { sm: "h-8 sm:h-12", md: "h-16 sm:h-24", lg: "h-28 sm:h-40" }[props.size || "md"];
  return (
    <div className={h} data-block="spacer">
      {props.rule && (
        <div className="h-full flex items-center justify-center px-6">
          <span className="block w-16 h-px bg-[#1A1A1A]/30" />
        </div>
      )}
    </div>
  );
}

// ---- Featured destinations ------------------------------------------
function BlockFeaturedDestinations({ props, api, mediaBase }) {
  const [items, setItems] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.get("/cms/destinations/published").then(({ data }) => {
      if (!alive) return;
      const list = Array.isArray(data) ? data : [];
      let chosen = list;
      const slugs = props.destination_slugs;
      const slugList = Array.isArray(slugs) ? slugs : (slugs || "").toString().split(",").map((s) => s.trim()).filter(Boolean);
      if (slugList.length > 0) {
        const bySlug = Object.fromEntries(list.map((d) => [d.slug, d]));
        chosen = slugList.map((s) => bySlug[s]).filter(Boolean);
      }
      setItems(chosen.slice(0, props.limit || 6));
    }).catch(() => setItems([]));
    return () => { alive = false; };
  }, [api, props.destination_slugs, props.limit]);
  return (
    <section className={`${cls.sectionPad}`} data-block="featured_destinations">
      <div className={cls.container}>
        {props.heading && (
          <h2 className={`${cls.serif} text-3xl sm:text-4xl text-center mb-3`}>{props.heading}</h2>
        )}
        {props.subline && (
          <p className={`${cls.italic} text-[17px] text-[#666] text-center mb-12 max-w-2xl mx-auto`}>{props.subline}</p>
        )}
        {!items ? (
          <p className="text-center text-[#999] font-mono text-xs uppercase tracking-[0.2em]">Loading destinations…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-[#999] font-mono text-xs uppercase tracking-[0.2em]">No destinations selected.</p>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {items.map((d) => {
              const hero = d.external_hero_url || (d.hero_image_id ? `${mediaBase}/${d.hero_image_id}` : null);
              return (
                <li key={d.id || d.slug}>
                  <Link to={`/destinations#${d.slug || ""}`} className="group block">
                    <div className="aspect-[4/3] overflow-hidden rounded-md bg-[#F5F2EE]">
                      {hero && (
                        <img src={hero} alt={d.destination_name} className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
                      )}
                    </div>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#999]">{d.region}</p>
                    <p className={`${cls.serif} text-xl mt-1`}>{d.destination_name}</p>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---- Featured journals ---------------------------------------------
function BlockFeaturedJournals({ props, api, mediaBase }) {
  const [items, setItems] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.get("/cms/journal/published").then(({ data }) => {
      if (!alive) return;
      const list = Array.isArray(data) ? data : [];
      let chosen = list;
      const slugs = props.journal_slugs;
      const slugList = Array.isArray(slugs) ? slugs : (slugs || "").toString().split(",").map((s) => s.trim()).filter(Boolean);
      if (slugList.length > 0) {
        const bySlug = Object.fromEntries(list.map((j) => [j.slug, j]));
        chosen = slugList.map((s) => bySlug[s]).filter(Boolean);
      }
      setItems(chosen.slice(0, props.limit || 3));
    }).catch(() => setItems([]));
    return () => { alive = false; };
  }, [api, props.journal_slugs, props.limit]);
  return (
    <section className={`${cls.sectionPad}`} data-block="featured_journals">
      <div className={cls.container}>
        {props.heading && (
          <h2 className={`${cls.serif} text-3xl sm:text-4xl text-center mb-3`}>{props.heading}</h2>
        )}
        {props.subline && (
          <p className={`${cls.italic} text-[17px] text-[#666] text-center mb-12 max-w-2xl mx-auto`}>{props.subline}</p>
        )}
        {!items ? (
          <p className="text-center text-[#999] font-mono text-xs uppercase tracking-[0.2em]">Loading journal…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-[#999] font-mono text-xs uppercase tracking-[0.2em]">No journal entries selected.</p>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {items.map((j) => {
              const cover = j.external_cover_url || (j.cover_image_id ? `${mediaBase}/${j.cover_image_id}` : null);
              return (
                <li key={j.id || j.slug}>
                  <Link to={`/blog/${j.slug}`} className="group block">
                    <div className="aspect-[4/3] overflow-hidden rounded-md bg-[#F5F2EE]">
                      {cover && (
                        <img src={cover} alt={j.title} className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
                      )}
                    </div>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#999]">{j.category || "The Journal"}</p>
                    <p className={`${cls.serif} text-xl mt-1 leading-snug`}>{j.title}</p>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---- Service cards --------------------------------------------------
function BlockServiceCards({ props }) {
  const items = Array.isArray(props.items) ? props.items : [];
  return (
    <section className={`${cls.sectionPad}`} data-block="service_cards">
      <div className={cls.container}>
        {props.heading && (
          <h2 className={`${cls.serif} text-3xl sm:text-4xl text-center mb-3`}>{props.heading}</h2>
        )}
        {props.subline && (
          <p className={`${cls.italic} text-[17px] text-[#666] text-center mb-12 max-w-2xl mx-auto`}>{props.subline}</p>
        )}
        <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {items.map((it, i) => (
            <li key={i}>
              {it.image_url && (
                <div className="aspect-[4/3] overflow-hidden rounded-md bg-[#F5F2EE] mb-5">
                  <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" />
                </div>
              )}
              <p className={`${cls.serif} text-lg mb-2`}>{it.title}</p>
              {it.description && (
                <p className={`${cls.body} text-[14px] leading-relaxed`}>{it.description}</p>
              )}
              {it.href && (
                <Link to={it.href} className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] border-b border-[#1A1A1A] pb-0.5 hover:gap-2 transition-all">
                  Read more <ArrowRight className="size-3" strokeWidth={1.5} />
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ---- Testimonial ---------------------------------------------------
function BlockTestimonial({ props }) {
  return (
    <section className={`${cls.sectionPad}`} data-block="testimonial">
      <div className={`${cls.prose} text-center`}>
        {props.quote && (
          <p className={`${cls.italic} text-2xl sm:text-3xl text-[#1A1A1A] leading-snug`}>
            “{props.quote}”
          </p>
        )}
        {props.attribution && (
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-[#999]">
            {props.attribution}
            {props.context && <span className="text-[#bbb]"> · {props.context}</span>}
          </p>
        )}
      </div>
    </section>
  );
}

// ---- FAQ -----------------------------------------------------------
function BlockFAQ({ props }) {
  const items = Array.isArray(props.items) ? props.items : [];
  return (
    <section className={`${cls.sectionPad}`} data-block="faq">
      <div className="max-w-3xl mx-auto">
        {props.heading && (
          <h2 className={`${cls.serif} text-3xl sm:text-4xl text-center mb-3`}>{props.heading}</h2>
        )}
        {props.subline && (
          <p className={`${cls.italic} text-[17px] text-[#666] text-center mb-12 max-w-2xl mx-auto`}>{props.subline}</p>
        )}
        <dl className="divide-y divide-[#E8E4DC]">
          {items.map((it, i) => (
            <div key={i} className="py-6">
              <dt className={`${cls.serif} text-lg text-[#1A1A1A]`}>{it.question}</dt>
              <dd className={`${cls.body} text-[15px] mt-3 whitespace-pre-line`}>{it.answer}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

// ---- Gallery -------------------------------------------------------
function BlockGallery({ props }) {
  const items = Array.isArray(props.items) ? props.items : [];
  const layout = props.layout === "mosaic" ? "mosaic" : "grid";
  return (
    <section className={`${cls.sectionPad}`} data-block="gallery">
      <div className={cls.container}>
        {props.heading && (
          <h2 className={`${cls.serif} text-3xl sm:text-4xl text-center mb-3`}>{props.heading}</h2>
        )}
        {props.subline && (
          <p className={`${cls.italic} text-[17px] text-[#666] text-center mb-12 max-w-2xl mx-auto`}>{props.subline}</p>
        )}
        {layout === "mosaic" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-4">
            {items.map((it, i) => (
              <figure key={i} className={`relative ${i % 5 === 0 ? "sm:col-span-2 sm:row-span-2 aspect-square" : "aspect-square"}`}>
                <img src={it.image_url} alt={it.image_alt || ""} className="absolute inset-0 w-full h-full object-cover rounded-sm" />
                {it.caption && (
                  <figcaption className="mt-1 text-[11px] font-mono uppercase tracking-[0.18em] text-[#999]">{it.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 lg:gap-5">
            {items.map((it, i) => (
              <figure key={i}>
                <div className="aspect-[4/3] overflow-hidden rounded-sm bg-[#F5F2EE]">
                  <img src={it.image_url} alt={it.image_alt || ""} className="w-full h-full object-cover" />
                </div>
                {it.caption && (
                  <figcaption className="mt-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[#999]">{it.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ---- Special "slot" blocks — pure markers, actual content rendered by
//      the page-level wrapper because they pull live data (forms, grids).
function BlockInquiryForm() {
  return (
    <div data-block="inquiry_form" data-slot="inquiry_form">
      {/* In the editor / preview context, this renders as a quiet placeholder.
          In the live wrapper, the real Plan form is mounted at this slot. */}
      <div className="py-16 px-6">
        <div className="max-w-xl mx-auto px-6 py-8 border border-dashed border-[#bbb] rounded text-center">
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-[#999]">Inquiry form</p>
          <p className="mt-2 text-[13px] text-[#666] font-accent italic">
            The Plan inquiry form will render here on the live site.
          </p>
        </div>
      </div>
    </div>
  );
}

function BlockDestinationsGrid() {
  return (
    <div data-block="destinations_grid" data-slot="destinations_grid">
      <div className="py-16 px-6">
        <div className="max-w-3xl mx-auto px-6 py-8 border border-dashed border-[#bbb] rounded text-center">
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-[#999]">Destinations grid</p>
          <p className="mt-2 text-[13px] text-[#666] font-accent italic">
            The destinations grid with filters renders here on the live site.
          </p>
        </div>
      </div>
    </div>
  );
}

function BlockJournalGrid() {
  return (
    <div data-block="journal_grid" data-slot="journal_grid">
      <div className="py-16 px-6">
        <div className="max-w-3xl mx-auto px-6 py-8 border border-dashed border-[#bbb] rounded text-center">
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-[#999]">Journal grid</p>
          <p className="mt-2 text-[13px] text-[#666] font-accent italic">
            The full Journal listing renders here on the live site.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---- Registry -------------------------------------------------------
const RENDERERS = {
  hero: BlockHero,
  text: BlockText,
  image_text: BlockImageText,
  cta: BlockCTA,
  spacer: BlockSpacer,
  featured_destinations: BlockFeaturedDestinations,
  featured_journals: BlockFeaturedJournals,
  service_cards: BlockServiceCards,
  testimonial: BlockTestimonial,
  faq: BlockFAQ,
  gallery: BlockGallery,
  inquiry_form: BlockInquiryForm,
  destinations_grid: BlockDestinationsGrid,
  journal_grid: BlockJournalGrid,
};

class BlockErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="my-6 mx-6 p-4 border border-amber-400 bg-amber-50 text-amber-900 rounded text-sm font-mono">
          A block failed to render and was hidden.
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * SiteBlocksRenderer
 *
 * @param {Array}  blocks         — list of {id, type, visible, props}
 * @param {Object} api            — axios instance from `../../index`
 * @param {string} mediaBase      — base URL for /api/cms/media/file
 * @param {Object} slotOverrides  — optional { inquiry_form: <Component/>, ... }
 *                                  When present, the special-slot block types
 *                                  render the override component instead of
 *                                  the placeholder. Used by live wrappers.
 */
export default function SiteBlocksRenderer({ blocks, api, mediaBase, slotOverrides }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <>
      {blocks
        .filter((b) => b && b.visible !== false)
        .map((b) => {
          const override = slotOverrides && slotOverrides[b.type];
          if (override) {
            return <BlockErrorBoundary key={b.id}>{override}</BlockErrorBoundary>;
          }
          const R = RENDERERS[b.type];
          if (!R) return null;
          return (
            <BlockErrorBoundary key={b.id}>
              <R props={b.props || {}} api={api} mediaBase={mediaBase} />
            </BlockErrorBoundary>
          );
        })}
    </>
  );
}

export { RENDERERS as BLOCK_RENDERERS };
